/**
 * Narrator voice-line keys, shared by the browser runtime (voice playback) and
 * `scripts/tts-narrator.mjs` (offline TTS render). Dependency-free and erasable-syntax
 * only so Node can import this file directly with type stripping.
 *
 * - `voiceText(text)`  → what the TTS actually speaks (stage directions removed).
 * - `voiceKey(text)`   → 8-hex FNV-1a hash of voiceText → `public/audio/voice/<key>.mp3`.
 * - `burpFraction(text)` → where each `*burp*` lands in the spoken line (0..1).
 */

/**
 * `*word*` sequences that are sound effects / stage directions (not spoken). Anything else
 * wrapped in asterisks (e.g. `*carpet*`) is emphasis: the asterisks go, the words stay.
 */
const STAGE_DIRECTION =
  /^(?:burps?|gulps?|sniffs?|sighs?|coughs?|ahem|hic|hiccups?|yawns?|gasps?|groans?|laughs?|chuckles?|snorts?|slurps?|clears throat|pause|beat|static|zap|zzz+)$/i;

const STAR = /\*([^*\n]{1,40})\*/g;

function clean(text: string): string {
  return text
    .replace(STAR, (_m, inner: string) => (STAGE_DIRECTION.test(inner.trim()) ? ' ' : inner))
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?;:…])/g, '$1')
    .replace(/([,;:])(?=[,.!?;:…])/g, '')
    .trim();
}

/** The text the TTS should speak for a subtitle line. */
export function voiceText(text: string): string {
  return clean(text);
}

/** 8-hex-char FNV-1a (32-bit, over UTF-8 bytes) of `voiceText(text)`. */
export function voiceKey(text: string): string {
  const bytes = new TextEncoder().encode(voiceText(text));
  let h = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i];
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/**
 * For each `*burp*` in `text`, its approximate position in the spoken line as a 0..1
 * fraction (by character offset in the cleaned text). A burp at the very end → 1.
 */
export function burpFraction(text: string): number[] {
  const total = clean(text).length;
  const out: number[] = [];
  const re = /\*burps?\*/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (total === 0) {
      out.push(0);
      continue;
    }
    const before = clean(text.slice(0, m.index)).length;
    out.push(Math.min(1, Math.max(0, before / total)));
  }
  return out;
}
